import * as React from 'react';
import RSSelect, { components, type GroupBase, type Props as RSProps, type StylesConfig } from 'react-select';

import { getSemanticAttributes } from '../../internals/semanticProps';
import { useTheme } from '../../theme/ThemeProvider';

import { createWealthlaneReactSelectStyles } from './createWealthlaneReactSelectStyles';

export type SelectOption = { label: string; value: string; isDisabled?: boolean };

export type SelectProps<
  Option extends SelectOption = SelectOption,
  IsMulti extends boolean = false,
> = Omit<RSProps<Option, IsMulti, GroupBase<Option>>, 'styles' | 'theme'> & {
  semanticId?: string;
  /** When true, control border uses danger tokens (legacy error state). */
  error?: boolean;
  styles?: StylesConfig<Option, IsMulti, GroupBase<Option>>;
};

function mergeStyles<Option, IsMulti extends boolean>(
  base: StylesConfig<Option, IsMulti, GroupBase<Option>>,
  override?: StylesConfig<Option, IsMulti, GroupBase<Option>>,
): StylesConfig<Option, IsMulti, GroupBase<Option>> {
  if (override == null) return base;
  const keys = new Set([
    ...Object.keys(base),
    ...Object.keys(override),
  ]) as Set<keyof StylesConfig<Option, IsMulti, GroupBase<Option>>>;
  const merged: StylesConfig<Option, IsMulti, GroupBase<Option>> = { ...base };
  keys.forEach((key) => {
    const b = base[key];
    const o = override[key];
    if (typeof o === 'function' && typeof b === 'function') {
      (merged as Record<string, unknown>)[key as string] = (provided: unknown, state: unknown) => ({
        ...(b as (p: unknown, s: unknown) => Record<string, unknown>)(provided, state),
        ...(o as (p: unknown, s: unknown) => Record<string, unknown>)(provided, state),
      });
    } else if (typeof o === 'function') {
      (merged as Record<string, unknown>)[key as string] = o;
    } else if (o !== undefined) {
      (merged as Record<string, unknown>)[key as string] = o;
    }
  });
  return merged;
}

function WlDropdownIndicator(props: React.ComponentProps<typeof components.DropdownIndicator>) {
  if (props.hasValue) {
    return <></>;
  }
  return (
    <components.DropdownIndicator {...props}>
      <span className="material-symbols-rounded" aria-hidden>
        arrow_drop_down
      </span>
    </components.DropdownIndicator>
  );
}

function WlClearIndicator(props: React.ComponentProps<typeof components.ClearIndicator>) {
  return (
    <components.ClearIndicator {...props}>
      <span
        className="material-symbols-rounded"
        style={{ fontSize: '16px', fontWeight: 'bold' }}
        aria-hidden
      >
        close
      </span>
    </components.ClearIndicator>
  );
}

const defaultComponents = {
  DropdownIndicator: WlDropdownIndicator,
  ClearIndicator: WlClearIndicator,
};

/**
 * Theme-aware single/multi select on `react-select`, styled to match legacy `formSelect`
 * (portal menu, fixed positioning, indicators, grouped options).
 */
const SelectInner = React.forwardRef(function Select(
  {
    semanticId,
    error = false,
    styles: stylesProp,
    components: componentsProp,
    menuPortalTarget,
    menuPosition = 'fixed',
    menuPlacement = 'auto',
    menuShouldBlockScroll = true,
    isClearable = true,
    className,
    classNamePrefix = 'wl-select',
    minMenuHeight = 200,
    maxMenuHeight = 300,
    'aria-invalid': ariaInvalid,
    ...rest
  }: SelectProps<SelectOption, boolean>,
  ref: React.Ref<unknown>,
) {
  const { theme } = useTheme();
  const semantic = getSemanticAttributes(semanticId);
  const baseStyles = React.useMemo(
    () => createWealthlaneReactSelectStyles<SelectOption, boolean>(theme, { hasError: error }),
    [theme, error],
  );
  const styles = React.useMemo(() => mergeStyles(baseStyles, stylesProp), [baseStyles, stylesProp]);
  const mergedComponents = React.useMemo(
    () => ({
      ...defaultComponents,
      ...(componentsProp ?? {}),
    }),
    [componentsProp],
  );

  const portalTarget =
    menuPortalTarget !== undefined
      ? menuPortalTarget
      : typeof document !== 'undefined'
        ? document.body
        : null;

  const invalid = ariaInvalid ?? error;

  return (
    <div {...semantic} className={className} style={{ width: '100%', maxWidth: '100%' }}>
      <RSSelect
        ref={ref as never}
        classNamePrefix={classNamePrefix}
        styles={styles}
        components={mergedComponents}
        menuPosition={menuPosition}
        menuPlacement={menuPlacement}
        menuShouldBlockScroll={menuShouldBlockScroll}
        isClearable={isClearable}
        menuPortalTarget={portalTarget}
        minMenuHeight={minMenuHeight}
        maxMenuHeight={maxMenuHeight}
        aria-invalid={invalid}
        {...(rest as RSProps<SelectOption, boolean, GroupBase<SelectOption>>)}
      />
    </div>
  );
});

SelectInner.displayName = 'Select';

export const Select = SelectInner as <
  Option extends SelectOption = SelectOption,
  IsMulti extends boolean = false,
>(
  props: SelectProps<Option, IsMulti> & {
    ref?: React.Ref<unknown>;
  },
) => React.ReactElement | null;
